import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { IpApi } from '@/core/apis/ip_api';

import { RecacheStorageHandler } from '../storage_handler_cached';
import { IpAddressData, IpData } from './types';

type TUpdateIpDataResult = {
  ipv4: string;
  ipv6: string;
  isUpdated: boolean;
};

// Refactored IpDataHandler
export class IpDataHandler {
  private static readonly _storageHandler = new RecacheStorageHandler<IpData>(
    'ipData',
    {
      ipAddress: {
        v4: '',
        v6: '',
      },
      ipLocation: {
        cc: '',
        ct: '',
        rc: '',
        rg: '',
        zip: '',
      },
    },
    EBrowserStorageType.LOCALSTORAGE,
    1 * 60 * 1000, // 5 minutes
    // eslint-disable-next-line @typescript-eslint/unbound-method
    IpApi.get, // Recache callback
  );

  /**
   * Compares and updates IP address data, checking for changes between current and new values
   *
   * @param currentIPData - Optional current IP data object containing existing v4 and v6 addresses
   * @param ipv4 - Optional new IPv4 address to compare against current data
   * @param ipv6 - Optional new IPv6 address to compare against current data
   * @returns {TUpdateIpDataResult} Object containing:
   *  - ipv4: The resulting IPv4 address (either current or new)
   *  - ipv6: The resulting IPv6 address (either current or new)
   *  - isUpdated: Boolean indicating if any IP addresses were updated
   *
   * @example
   * const result = IpDataHandler.updateIPData(
   *   { ipAddress: { v4: '192.168.1.1', v6: '2001:db8::1' } },
   *   '192.168.1.2',
   *   '2001:db8::2'
   * );
   */
  static updateIPData(
    currentIPData?: IpAddressData,
    ipv4?: string,
    ipv6?: string,
  ): TUpdateIpDataResult {
    let isUpdated = false;
    if (!currentIPData) {
      // No current IP data and no provided data
      return {
        ipv4: ipv4 ?? '',
        ipv6: ipv6 ?? '',
        isUpdated,
      };
    }
    if (currentIPData.v4 !== ipv4) {
      ipv4 = currentIPData.v4;
      isUpdated = true;
    }
    if (currentIPData.v6 !== ipv6) {
      ipv6 = currentIPData.v6;
      isUpdated = true;
    }
    return {
      ipv4: ipv4 ?? '',
      ipv6: ipv6 ?? '',
      isUpdated,
    };
  }

  static async initOrLoad(): Promise<IpData> {
    return this._storageHandler.getAsync();
  }
}
